import { useContext, useMemo } from 'react';

import Dropzone from '@atom/components/common/dropzone/Dropzone';
import colors from '@atom/styles/colors';
import { CsvErrorItem } from '@atom/types/error';
import {
  BudgetTemplateType,
  getBudgetTemplateEndpoint,
} from '@atom/utilities/endpoints';
import { buildEndpointWithParamsFromData } from '@atom/utilities/requestUtilities';

import BudgetDetailContext from '../BudgetDetailContext';

import classes from './budgetUpload.module.css';

export interface Props {
  type: BudgetTemplateType;
  file: File;
  onFileChange: (file?: File) => void;
  validating: boolean;
  errors: CsvErrorItem[];
  resetState: () => void;
}

const styles = {
  selectText: {
    color: colors.brand.blue,
    cursor: 'pointer',
    padding: '0.15rem',
  },
};

const BudgetUploadMain = ({
  type,
  file,
  onFileChange,
  validating,
  errors,
  resetState,
}: Props) => {
  const { parentBudgetUnit } = useContext(BudgetDetailContext);

  const filterData = { parentBudgetUnitId: parentBudgetUnit?.id };

  const getDownloadEndpoint = (templateType: BudgetTemplateType): string => {
    return buildEndpointWithParamsFromData(
      getBudgetTemplateEndpoint(templateType),
      filterData,
    );
  };

  const authorizedTemplateHref = useMemo(() => {
    return getDownloadEndpoint('authorizedBudget');
  }, [parentBudgetUnit]);

  const templateHref = useMemo(() => {
    return getDownloadEndpoint('budgetItems');
  }, [parentBudgetUnit]);

  return (
    <>
      <p>
        You can update budget in bulk by editing data on
        <span
          onClick={() =>
            window.open(
              type === 'authorizedBudget'
                ? authorizedTemplateHref
                : templateHref,
            )
          }
          style={styles.selectText}
        >
          this template
        </span>
        and upload to the system.
      </p>

      <Dropzone
        onFilesChange={files => onFileChange(files[0] || null)}
        files={file ? [file] : []}
        onFilesRemove={() => resetState()}
        accept={{ 'text/csv': ['.csv'] }}
        loading={validating}
        loadingText="Validating..."
      />

      {!!errors.length && (
        <ul className={classes.errorList}>
          {errors.map(err => (
            <li>
              {err.row > 0
                ? `Row ${err.row} Column "${err.column}" ${err.message}`
                : err.message}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default BudgetUploadMain;
